/** Controls above the «Сейчас» image: age, refraction (SPH / CYL / AXIS) and eye conditions (D-025). */

import { FEATURES } from '../features';
import { formatD, formatSigned, t, tl, type Lang, type StringKey } from '../i18n';
import { describeRefraction, toMinusCylinder } from '../optics/refraction';
import { clamp } from '../optics/types';
import { state, update, type State } from '../state';
import { h } from './dom';

export interface Controls {
  el: HTMLElement;
  sync(): void;
}

type NumberKey = 'age' | 'sph' | 'cyl' | 'axis';
type StageKey = 'cataract' | 'keratoconus' | 'glaucoma' | 'amd' | 'dr' | 'hr' | 'rp' | 'amblyopia';

const ROMAN = ['I', 'II', 'III', 'IV', 'V'];
/** Aphakic eyes usually measure about this sphere; used as the starting value when aphakia is switched on. */
const APHAKIC_SPH = 11;

/** Parse a clinician-typed number: "+1,25", "−2.5", "-0.75 D", "180°". */
export function parseInputNumber(text: string): number | null {
  const s = text.trim().replace(/\s+/g, '').replace(/[−–—]/g, '-').replace(',', '.').replace(/(d|дптр|д|°)$/i, '');
  if (!/^[+-]?(\d+\.?\d*|\.\d+)$/.test(s)) return null;
  const v = Number(s);
  return Number.isFinite(v) ? v : null;
}

interface Stepper {
  el: HTMLElement;
  sync(): void;
}

/** A labelled number with − / + buttons and a typed field. */
function stepper(
  key: NumberKey,
  label: string,
  range: { min: number; max: number; step: number; buttonStep: number },
  format: (v: number) => string,
): Stepper {
  const set = (v: number): void => {
    const snapped = clamp(Math.round(v / range.step) * range.step, range.min, range.max);
    update({ [key]: snapped === 0 ? 0 : snapped } as Partial<State>);
  };
  const input = h('input', {
    type: 'text', id: `f-${key}`, class: 'step-input', inputmode: 'decimal', autocomplete: 'off', spellcheck: 'false',
    onchange: () => {
      const v = parseInputNumber(input.value);
      if (v !== null) set(v);
      input.value = format(state[key]);
    },
  });
  const dec = h('button', { type: 'button', class: 'step-btn', 'aria-label': `${label} −`, text: '−', onclick: () => set(state[key] - range.buttonStep) });
  const inc = h('button', { type: 'button', class: 'step-btn', 'aria-label': `${label} +`, text: '+', onclick: () => set(state[key] + range.buttonStep) });
  const el = h('div', { class: 'field' },
    h('label', { class: 'field-label', for: input.id, text: label }),
    h('div', { class: 'stepper' }, dec, input, inc),
  );
  return {
    el,
    sync() {
      if (document.activeElement !== input) input.value = format(state[key]);
    },
  };
}

interface Condition {
  chip: HTMLLabelElement;
  row: HTMLElement | null;
  sync(): void;
}

/** A condition chip; when it is on, a row of stage buttons appears under the chips. */
function stageCondition(
  lang: Lang,
  key: StageKey,
  label: string,
  stages: readonly string[],
  defaultStage: number,
  sub?: readonly string[],
  hint?: string,
): Condition {
  let remembered = state[key] || defaultStage;
  const input = h('input', {
    type: 'checkbox', id: `chip-${key}`,
    onchange: () => update({ [key]: input.checked ? remembered : 0 } as Partial<State>),
  });
  const chip = h('label', { class: 'chip', for: input.id }, input, h('span', { text: label }));
  const buttons = stages.map((name, i) =>
    h('button', {
      type: 'button', class: 'seg-btn stage-btn', 'data-stage': i + 1,
      onclick: () => {
        remembered = i + 1;
        update({ [key]: remembered } as Partial<State>);
      },
    },
    h('span', { class: 'stage-num', text: ROMAN[i] ?? String(i + 1) }),
    h('span', { class: 'stage-name' }, name, sub?.[i] ? h('small', { text: sub[i] }) : null)),
  );
  const row = h('div', { class: 'stage-row' },
    h('div', { class: 'stage-title', text: `${label} — ${t(lang, 'stage').toLowerCase()}` }),
    h('div', { class: 'seg' }, ...buttons),
    hint ? h('p', { class: 'hint', text: hint }) : null,
  );
  return {
    chip,
    row,
    sync() {
      const v = state[key];
      input.checked = v > 0;
      chip.classList.toggle('on', v > 0);
      row.hidden = !(v > 0);
      buttons.forEach((b, i) => {
        b.classList.toggle('on', v === i + 1);
        b.setAttribute('aria-pressed', v === i + 1 ? 'true' : 'false');
      });
    },
  };
}

export function buildPatientControls(): Controls {
  const lang = state.lang;

  const age = stepper('age', t(lang, 'age'), { min: 10, max: 90, step: 1, buttonStep: 1 }, (v) => String(Math.round(v)));
  const refr = [
    stepper('sph', 'SPH', { min: -20, max: 20, step: 0.25, buttonStep: 0.25 }, formatSigned),
    stepper('cyl', 'CYL', { min: -8, max: 8, step: 0.25, buttonStep: 0.25 }, formatSigned),
    stepper('axis', 'AXIS', { min: 0, max: 180, step: 1, buttonStep: 5 }, (v) => `${Math.round(v)}°`),
  ];
  const summary = h('p', { class: 'info', id: 'refr-summary' });

  // Aphakia: an on/off state of the eye; switching it on starts from a typical aphakic sphere.
  let sphBefore: number | null = null;
  let sphSet: number | null = null;
  const aphInput = h('input', {
    type: 'checkbox', id: 'chip-aphakia',
    onchange: () => {
      if (aphInput.checked) {
        sphBefore = state.sph;
        const patch: Partial<State> = { aphakia: true };
        if (state.sph < 4) {
          patch.sph = APHAKIC_SPH;
          sphSet = APHAKIC_SPH;
        } else {
          sphSet = null;
        }
        update(patch);
      } else {
        const patch: Partial<State> = { aphakia: false };
        if (sphSet !== null && sphBefore !== null && state.sph === sphSet) patch.sph = sphBefore;
        sphBefore = null;
        sphSet = null;
        update(patch);
      }
    },
  });
  const aphChip = h('label', { class: 'chip', for: aphInput.id, title: t(lang, 'cond.aphakia.info') },
    aphInput, h('span', { text: t(lang, 'cond.aphakia') }));
  const aphRow = h('p', { class: 'stage-row info', text: t(lang, 'cond.aphakia.info') });

  const c = FEATURES.conditions;
  const cataract = stageCondition(lang, 'cataract', t(lang, 'cond.cataract'), tl(lang, 'cond.cataract.stages'), 2);
  const conditions: Condition[] = [];
  if (c.cataract) conditions.push(cataract);
  if (c.glaucoma) conditions.push(stageCondition(lang, 'glaucoma', t(lang, 'cond.glaucoma'), tl(lang, 'cond.glaucoma.stages'), 2));
  if (c.amd) conditions.push(stageCondition(lang, 'amd', t(lang, 'cond.amd'), tl(lang, 'cond.amd.stages'), 2));
  if (c.dr) conditions.push(stageCondition(lang, 'dr', t(lang, 'cond.dr'), tl(lang, 'cond.dr.stages'), 2));
  if (c.hr) conditions.push(stageCondition(lang, 'hr', t(lang, 'cond.hr'), tl(lang, 'cond.hr.stages'), 3));
  if (c.keratoconus) conditions.push(stageCondition(lang, 'keratoconus', t(lang, 'cond.kc'), tl(lang, 'cond.kc.stages'), 2));
  if (c.rp) conditions.push(stageCondition(lang, 'rp', t(lang, 'cond.rp'), tl(lang, 'cond.rp.stages'), 2));
  if (c.amblyopia) {
    conditions.push(stageCondition(lang, 'amblyopia', t(lang, 'cond.amblyopia'), tl(lang, 'cond.amblyopia.stages'), 3,
      tl(lang, 'cond.amblyopia.visus'), t(lang, 'cond.amblyopia.hint')));
  }

  const el = h('section', { class: 'card ctrl ctrl-now' },
    h('h2', { text: t(lang, 'step.now') }),
    h('div', { class: 'fields' }, age.el),
    h('div', { class: 'group-title', text: t(lang, 'refr.title') }),
    h('div', { class: 'fields' }, ...refr.map((r) => r.el)),
    summary,
    h('p', { class: 'hint', text: t(lang, 'refr.hint') }),
    h('div', { class: 'group-title', text: t(lang, 'cond.title') }),
    h('div', { class: 'chips' }, ...conditions.map((x) => x.chip), c.aphakia ? aphChip : null),
    ...conditions.map((x) => x.row),
    c.aphakia ? aphRow : null,
  );

  function syncSummary(s: State): void {
    const m = toMinusCylinder({ sph: s.sph, cyl: s.cyl, axis: s.axis });
    const d = describeRefraction(m.sph, m.cyl);
    const kindKey = (d.kind === 'emmetropia' || d.kind === 'myopia' || d.kind === 'hyperopia'
      ? `refr.kind.${d.kind}` : `astig.${d.kind}`) as StringKey;
    let text = `${t(s.lang, kindKey)} · ${t(s.lang, 'refr.se', { x: formatD(d.se) })}`;
    if (m.cyl !== 0) text += ` · ${t(s.lang, 'refr.meridians', { m1: formatD(d.m1), m2: formatD(d.m2) })}`;
    if (s.cyl > 0) text += ` · ${formatSigned(m.sph)} ${formatSigned(m.cyl)} × ${m.axis}°`;
    summary.textContent = text;
  }

  function sync(): void {
    const s = state;
    age.sync();
    for (const r of refr) r.sync();
    syncSummary(s);
    aphInput.checked = s.aphakia;
    aphChip.classList.toggle('on', s.aphakia);
    aphRow.hidden = !s.aphakia;
    for (const x of conditions) x.sync();
    cataract.chip.hidden = s.aphakia;
  }

  sync();
  return { el, sync };
}
